/*
  /apply — Foundation Studio Program: high-conversion sales experience.

  Flow:
      1. Hero (dark) with real product composition
      2. Six-stage workflow (light / warm)
      3. Design feature — DesignMock (dark)
      4. Visualise feature — VisualiseMock (dark)
      5. Price feature — PriceMock (light)
      6. Proposal + Approval — ProposalPortalMock (dark)
      7. Built-in head start — starter materials + templates (light / warm)
      8. Before / With Luxe comparison (dark)
      9. Foundation Studio program (dark architectural)
     10. Country-aware application (light / warm)   — REUSES existing form
     11. Country-specific success screens           — REUSES existing SuccessPanel
     12. Footer (dark)

  Every submission still goes through the same
  POST /api/public/beta-applications endpoint via the existing
  <ApplicationForm/>. This file is presentation only.
*/

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import subtendV2_1800 from "@/assets/apply/subtend-v2-concept-1800w.webp";
import subtendV2_1200 from "@/assets/apply/subtend-v2-concept-1200w.webp";
import subtendV2_780  from "@/assets/apply/subtend-v2-concept-780w.webp";

import {
  COLORS, SERIF, SANS,
  ApplicationForm, SuccessPanel, CountrySelector, trackEvent,
} from "./_form";
import {
  DesignMock, VisualiseMock, PriceMock, ProposalPortalMock,
  TemplateGalleryMock, MaterialsGrid, HeroProductComposition,
} from "./_mocks";

// ── Tiny reveal-on-scroll (uses IntersectionObserver, respects
// prefers-reduced-motion). Purely cosmetic — content is always
// rendered so bots + a11y trees see everything.
function Reveal({ children, delay = 0, as: Tag = "div", style, className, testid }) {
  const [visible, setVisible] = useState(false);
  const [ref, setRef] = useState(null);
  useEffect(() => {
    if (!ref) return;
    const reduce = typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -60px 0px" });
    io.observe(ref);
    return () => io.disconnect();
  }, [ref]);
  return (
    <Tag ref={setRef}
         data-testid={testid}
         className={className}
         style={{
           ...style,
           opacity: visible ? 1 : 0,
           transform: visible ? "translateY(0)" : "translateY(14px)",
           transition: `opacity .55s ease ${delay}ms, transform .55s ease ${delay}ms`,
         }}>
      {children}
    </Tag>
  );
}

// ── Section wrappers ──────────────────────────────────────────────
function Section({ id, tone = "dark", children, padY = 96, testid }) {
  const bg = tone === "dark" ? COLORS.bgDark
           : tone === "deep" ? COLORS.bgDeep
           : tone === "warm" ? "#F5EFDD"
           :                    COLORS.ivory;
  return (
    <section id={id} data-testid={testid}
             style={{ background: bg, position: "relative" }}>
      <div className="apply-shell" style={{ padding: `${padY}px 28px` }}>
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, tone = "gold" }) {
  return (
    <div style={{ ...SANS, letterSpacing: "0.28em", textTransform: "uppercase",
                  color: tone === "gold" ? COLORS.gold : COLORS.goldDeep,
                  fontSize: 11.5, fontWeight: 700, marginBottom: 14 }}>
      {children}
    </div>
  );
}

function H2({ children, dark }) {
  return (
    <h2 style={{ ...SERIF, color: dark ? COLORS.ivory : COLORS.ink,
                 fontSize: "clamp(30px, 4.2vw, 46px)", lineHeight: 1.08,
                 fontWeight: 400, margin: "0 0 18px", maxWidth: 780 }}>
      {children}
    </h2>
  );
}

function Lede({ children, dark }) {
  return (
    <p style={{ ...SANS, color: dark ? "rgba(244,239,228,0.72)" : COLORS.inkSoft,
                fontSize: 17, lineHeight: 1.7, margin: 0, maxWidth: 640 }}>
      {children}
    </p>
  );
}

// ── Smooth-scroll helper ─────────────────────────────────────────
const scrollToApply = (country) => {
  const el = document.getElementById("apply-form");
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  // Preselect country via CustomEvent — the form component listens
  // and switches to the AU or UK form. Safe: the underlying
  // submission logic is unchanged; only the initial view.
  if (country) {
    setTimeout(() => window.dispatchEvent(new CustomEvent("luxe:apply-preselect",
                                                          { detail: { country }})), 250);
    trackEvent(country === "AU" ? "au_beta_cta_click" : "uk_early_access_cta_click", {});
  } else {
    trackEvent("hero_apply_click", {});
  }
};

const scrollToId = (id, eventName) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (eventName) trackEvent(eventName, {});
};

// ── Public header ────────────────────────────────────────────────
function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header data-testid="apply-header"
            style={{ ...SANS, position: "sticky", top: 0, zIndex: 40,
                     background: scrolled ? "rgba(10,9,8,0.86)" : "transparent",
                     backdropFilter: scrolled ? "blur(12px)" : "none",
                     borderBottom: scrolled ? `1px solid rgba(200,169,126,0.15)` : "1px solid transparent",
                     transition: "background .3s, border-color .3s, backdrop-filter .3s" }}>
      <div className="apply-shell"
           style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", padding: "18px 28px",
                    color: COLORS.ivory }}>
        <Link to="/" data-testid="apply-brand"
              aria-label="Subtend Studio"
              style={{ ...SANS, textDecoration: "none", color: COLORS.ivory,
                       display: "flex", flexDirection: "column",
                       letterSpacing: "0.28em", fontWeight: 700,
                       fontSize: 12, lineHeight: 1.05 }}>
          <span>SUBTEND</span>
          <span style={{ color: COLORS.gold }}>STUDIO</span>
        </Link>
        <nav className="apply-nav-desktop"
             style={{ display: "flex", gap: 28, alignItems: "center" }}>
          {[
            { label: "Features",    id: "features",    ev: "nav_features" },
            { label: "How It Works",id: "workflow",    ev: "nav_workflow" },
            { label: "For Studios", id: "foundation",  ev: "nav_studios" },
            { label: "Private Beta",id: "foundation",  ev: "nav_beta" },
          ].map(item => (
            <button key={item.label}
                    onClick={() => scrollToId(item.id, item.ev)}
                    style={{ ...SANS, background: "transparent", border: "none",
                             color: "rgba(244,239,228,0.82)", fontSize: 13,
                             letterSpacing: "0.06em", fontWeight: 500,
                             cursor: "pointer", padding: 0 }}>
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/login" data-testid="apply-header-login"
                style={{ ...SANS, textDecoration: "none",
                         color: "rgba(244,239,228,0.72)",
                         fontSize: 13, letterSpacing: "0.06em" }}>
            Login
          </Link>
          <button data-testid="apply-header-cta"
                  onClick={() => scrollToApply()}
                  style={{ ...SANS, background: COLORS.gold, color: COLORS.bgDark,
                           border: "none", padding: "10px 18px", fontSize: 12.5,
                           letterSpacing: "0.06em", fontWeight: 600, cursor: "pointer" }}>
            Apply for Access →
          </button>
        </div>
      </div>
    </header>
  );
}

// ── Hero ─────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section data-testid="apply-hero"
             style={{ background: COLORS.bgDark, position: "relative", overflow: "hidden" }}>
      {/* Ambient warmth */}
      <div aria-hidden="true"
           style={{ position: "absolute", inset: 0, pointerEvents: "none",
                    background:
                      "radial-gradient(1200px 500px at 5% 0%, rgba(200,169,126,0.14), transparent 60%)," +
                      "radial-gradient(900px 500px at 100% 100%, rgba(176,101,76,0.10), transparent 60%)" }}/>
      <div className="apply-shell" style={{ padding: "72px 28px 96px", position: "relative" }}>
        <div className="apply-hero-grid"
             style={{ display: "grid", gap: 42, alignItems: "center" }}>
          <Reveal>
            <div style={{ ...SANS, display: "inline-flex", gap: 8, alignItems: "center",
                          border: "1px solid rgba(200,169,126,0.28)",
                          padding: "6px 12px", marginBottom: 22,
                          color: COLORS.gold, fontSize: 11, letterSpacing: "0.18em",
                          fontWeight: 600, textTransform: "uppercase" }}>
              <span style={{ width: 6, height: 6, background: COLORS.gold, borderRadius: "50%" }}/>
              Built for media-wall businesses
            </div>
            <h1 style={{ ...SERIF, color: COLORS.ivory,
                         fontSize: "clamp(44px, 7.5vw, 84px)",
                         fontWeight: 400, lineHeight: 1.02, margin: "0 0 22px",
                         letterSpacing: "-0.01em" }}>
              Design.<br className="apply-hero-br-mobile"/>{" "}
              Visualise.<br/>
              Price.<br className="apply-hero-br-mobile"/>{" "}
              <span style={{ fontStyle: "italic", color: COLORS.gold }}>Sell.</span>
            </h1>
            <p style={{ ...SANS, color: "rgba(244,239,228,0.82)",
                        fontSize: 19, lineHeight: 1.6, maxWidth: 560, margin: "0 0 18px" }}>
              Subtend Studio brings your media-wall workflow into one place — from the first client brief and wall design through 3D visualisation, pricing, proposal and approval.
            </p>
            <p style={{ ...SANS, color: "rgba(244,239,228,0.6)",
                        fontSize: 14.5, lineHeight: 1.7, maxWidth: 560, margin: "0 0 34px" }}>
              No more piecing the same project together across drawing software, spreadsheets, documents and email.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 30 }}>
              <button data-testid="apply-hero-cta"
                      onClick={() => scrollToApply()}
                      style={{ ...SANS, background: COLORS.gold, color: COLORS.bgDark,
                               border: "none", padding: "16px 26px", fontSize: 14,
                               fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer" }}>
                Apply for Foundation Access ↓
              </button>
              <button data-testid="apply-hero-secondary"
                      onClick={() => scrollToId("workflow", "workflow_viewed")}
                      style={{ ...SANS, background: "transparent", color: COLORS.ivory,
                               border: `1px solid rgba(244,239,228,0.28)`,
                               padding: "16px 26px", fontSize: 14, fontWeight: 500,
                               letterSpacing: "0.06em", cursor: "pointer" }}>
                See How It Works
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24,
                          borderTop: "1px solid rgba(200,169,126,0.18)", paddingTop: 20 }}>
              <div style={{ ...SANS, fontSize: 13, color: "rgba(244,239,228,0.78)",
                            display: "inline-flex", alignItems: "center", gap: 10 }}>
                <span style={{ ...SANS, fontSize: 10, fontWeight: 700,
                                letterSpacing: "0.16em", padding: "3px 7px",
                                border: `1px solid ${COLORS.gold}`, color: COLORS.gold }}>AU</span>
                <span style={{ color: COLORS.gold, fontWeight: 600 }}>Australia</span>
                <span style={{ color: "rgba(244,239,228,0.55)" }}> — Paid Private Beta</span>
              </div>
              <div style={{ ...SANS, fontSize: 13, color: "rgba(244,239,228,0.78)",
                            display: "inline-flex", alignItems: "center", gap: 10 }}>
                <span style={{ ...SANS, fontSize: 10, fontWeight: 700,
                                letterSpacing: "0.16em", padding: "3px 7px",
                                border: `1px solid ${COLORS.gold}`, color: COLORS.gold }}>GB</span>
                <span style={{ color: COLORS.gold, fontWeight: 600 }}>United Kingdom</span>
                <span style={{ color: "rgba(244,239,228,0.55)" }}> — Early Access</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={140}>
            <HeroProductComposition/>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ── Workflow section ─────────────────────────────────────────────
function WorkflowSection() {
  const stages = [
    { n: "01", h: "BRIEF",     b: "Capture the room, measurements, project images and client requirements." },
    { n: "02", h: "DESIGN",    b: "Build from scratch or start with a professionally prepared media-wall design." },
    { n: "03", h: "VISUALISE", b: "Move into 3D so your client can understand what they're buying." },
    { n: "04", h: "PRICE",     b: "Materials, labour, trades, sell price and margin stay connected to the design." },
    { n: "05", h: "PROPOSAL",  b: "Turn the selected concept into a polished client-safe proposal." },
    { n: "06", h: "APPROVAL",  b: "Keep feedback, requested changes and acceptance attached to the project." },
  ];
  return (
    <Section id="workflow" tone="warm" testid="apply-section-workflow" padY={96}>
      <Reveal>
        <Eyebrow tone="deep">One Project. One Connected Workflow.</Eyebrow>
        <H2>Everything you need to take an enquiry from idea to approval.</H2>
        <Lede>
          Subtend Studio follows the way premium media-wall projects are actually designed, priced, presented and sold.
        </Lede>
      </Reveal>
      <div className="apply-workflow-grid"
           style={{ display: "grid", gap: 18, marginTop: 46 }}>
        {stages.map((s, i) => (
          <Reveal key={s.n} delay={i * 60}>
            <div style={{ background: "#FFFDF6", border: `1px solid ${COLORS.cardEdge}`,
                          padding: "22px 22px 24px", height: "100%",
                          display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ ...SERIF, fontSize: 38, color: COLORS.gold, lineHeight: 1, fontWeight: 400 }}>
                {s.n}
              </div>
              <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.20em",
                            fontWeight: 700, color: COLORS.goldDeep, marginTop: 4 }}>{s.h}</div>
              <div style={{ ...SANS, fontSize: 13.5, color: COLORS.inkSoft, lineHeight: 1.6 }}>
                {s.b}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div style={{ ...SERIF, fontStyle: "italic", fontSize: 22,
                      color: COLORS.goldDeep, marginTop: 44, textAlign: "center" }}>
          Brief → Design → Visualise → Price → Proposal → Approval
        </div>
      </Reveal>
    </Section>
  );
}

// ── Design feature (dark) ────────────────────────────────────────
function DesignFeatureSection() {
  return (
    <Section id="features" tone="dark" testid="apply-section-design" padY={110}>
      <div className="apply-feature-grid"
           style={{ display: "grid", gap: 48, alignItems: "center" }}>
        <Reveal>
          <Eyebrow>Start with the design</Eyebrow>
          <H2 dark>Design the wall. Not the software.</H2>
          <Lede dark>
            Start with the client's wall dimensions, build the concept from scratch or use a curated media-wall template, then refine the design around the project.
          </Lede>
          <div style={{ ...SERIF, fontStyle: "italic", color: COLORS.gold,
                        fontSize: 20, marginTop: 22, maxWidth: 520 }}>
            Start faster without turning every project into the same design.
          </div>
        </Reveal>
        <Reveal delay={120}>
          <DesignMock/>
        </Reveal>
      </div>
    </Section>
  );
}

// ── Visualise feature (dark) ─────────────────────────────────────
function VisualiseFeatureSection() {
  return (
    <Section tone="deep" testid="apply-section-visualise" padY={110}>
      <div className="apply-feature-grid apply-feature-reverse"
           style={{ display: "grid", gap: 48, alignItems: "center" }}>
        <Reveal delay={120}>
          <VisualiseMock/>
        </Reveal>
        <Reveal>
          <Eyebrow>Design it in 2D · See it in 3D</Eyebrow>
          <H2 dark>Design it in 2D. See it in 3D.</H2>
          <Lede dark>
            Build the media-wall concept around the client's actual wall, then move into the Subtend Studio 3D environment to understand proportions, layout and finish direction before the proposal is presented — no rebuilding the design in another application.
          </Lede>
          <div style={{ ...SERIF, fontStyle: "italic", color: COLORS.gold,
                        fontSize: 20, marginTop: 22, maxWidth: 520 }}>
            Design once. Carry it through the workflow.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 24 }}>
            <span style={{ ...SANS, display: "inline-flex", gap: 8, alignItems: "center",
                            padding: "6px 12px", background: "rgba(200,169,126,0.16)",
                            border: `1px solid ${COLORS.gold}`, color: COLORS.gold,
                            fontSize: 10.5, letterSpacing: "0.16em", fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, background: COLORS.gold, borderRadius: "50%" }}/>
              2D DESIGN · AVAILABLE
            </span>
            <span style={{ ...SANS, display: "inline-flex", gap: 8, alignItems: "center",
                            padding: "6px 12px", background: "rgba(200,169,126,0.16)",
                            border: `1px solid ${COLORS.gold}`, color: COLORS.gold,
                            fontSize: 10.5, letterSpacing: "0.16em", fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, background: COLORS.gold, borderRadius: "50%" }}/>
              3D VISUALISE · AVAILABLE
            </span>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ── 2D → 3D → AI PROGRESSION (immediately beneath hero) ──────────
// This is the first major visual product-reveal after the hero.
// Stage 1 = real Subtend Studio 2D Design UI mock (SAME project as hero)
// Stage 2 = real Subtend Studio 3D Visualise UI mock (SAME project)
// Stage 3 = premium photorealistic media-wall reference — labelled as
//           a CONCEPT PREVIEW for the planned V2 AI Visualisation.
// Every layer reinforces the truth: 2D + interactive 3D ship in the
// Foundation Beta today; AI Visualisation is future work.
function V2AiTeaserSection() {
  // Custom Subtend Studio V2 concept image (owner-supplied premium
  // media-wall reference). Delivered as three locally-hosted WebP
  // variants (780/1200/1800w) via a responsive srcSet. NEVER hotlinked.
  const AI_IMG = subtendV2_1800;
  const AI_SRCSET = `${subtendV2_780} 780w, ${subtendV2_1200} 1200w, ${subtendV2_1800} 1800w`;
  return (
    <Section tone="deep" testid="apply-section-v2-teaser" padY={110}>
      <Reveal>
        <Eyebrow>From design to presentation</Eyebrow>
        <H2 dark>
          One design. <span style={{ fontStyle: "italic", color: COLORS.gold }}>Three levels of clarity.</span>
        </H2>
        <Lede dark>
          Subtend Studio is being built around one connected project workflow — from the working design, through interactive 3D visualisation, to the premium client presentation capabilities we're developing for V2.
        </Lede>
      </Reveal>

      {/* Three-stage progression — same Aldridge Residence media wall
          at three levels of visual fidelity. */}
      <div className="apply-v2-stages" style={{ display: "grid",
             gap: 22, marginTop: 44,
             gridTemplateColumns: "minmax(0, 1fr)" }}>
        {/* Stage 1 · Design (2D) */}
        <Reveal delay={80}>
          <StageBlock kicker="01 · Design" title="Build it in 2D."
                      body="Create the media-wall concept around the client's actual wall dimensions, then add the TV, cabinetry, fireplace, materials and finishes."
                      statusLabel="Available in Foundation Beta"
                      statusTone="available">
            <div style={{ padding: "18px 18px 0", background: "#0F0E0C",
                          borderRadius: 2 }}>
              <DesignMock/>
            </div>
          </StageBlock>
        </Reveal>

        {/* Stage 2 · Visualise (3D) — larger, more dimensional */}
        <Reveal delay={160}>
          <StageBlock kicker="02 · Visualise" title="See it in 3D."
                      body="Move the same working design into an interactive 3D view to understand proportions, layout and material direction — the 2D drawing and 3D scene share the same canonical project so nothing gets rebuilt."
                      statusLabel="Available in Foundation Beta"
                      statusTone="available"
                      emphasis>
            <div style={{ padding: "18px 18px 0", background: "#0F0E0C",
                          borderRadius: 2 }}>
              <VisualiseMock/>
            </div>
          </StageBlock>
        </Reveal>

        {/* Stage 3 · Present (AI Visualisation) — largest, emotional */}
        <Reveal delay={240}>
          <StageBlock kicker="03 · Present" title="Show the vision before the build begins."
                      body="Subtend Studio's upcoming AI Visualisation is being developed to turn the working media-wall concept into a premium, client-ready design image — helping customers picture the finished space before construction begins."
                      statusLabel="Coming in V2"
                      statusTone="v2"
                      emphasis
                      hero>
            <div style={{ position: "relative", aspectRatio: "3 / 2",
                          overflow: "hidden", background: "#100E0C" }}>
              <img
                src={AI_IMG}
                srcSet={AI_SRCSET}
                sizes="(max-width: 900px) 92vw, 1080px"
                alt="Concept preview of a premium media-wall presentation representing the planned Subtend Studio V2 AI Visualisation experience."
                loading="lazy"
                decoding="async"
                width="1800"
                height="1200"
                style={{ position: "absolute", inset: 0, width: "100%",
                         height: "100%", objectFit: "cover", display: "block" }}/>
              <div aria-hidden="true"
                   style={{ position: "absolute", inset: 0,
                            background: "linear-gradient(180deg, rgba(16,14,12,0.68) 0%, rgba(16,14,12,0) 32%, rgba(16,14,12,0) 62%, rgba(16,14,12,0.62) 100%)",
                            pointerEvents: "none" }}/>
              <div style={{ position: "absolute", top: 16, left: 16,
                            display: "inline-flex", flexDirection: "column",
                            gap: 4, padding: "10px 14px",
                            background: "rgba(15,14,12,0.85)",
                            border: `1px solid ${COLORS.gold}`,
                            backdropFilter: "blur(6px)" }}>
                <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.24em",
                              color: COLORS.gold, fontWeight: 700,
                              textTransform: "uppercase" }}>Coming in V2</div>
                <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.14em",
                              color: COLORS.ivory, fontWeight: 600 }}>
                  AI Visualisation
                </div>
              </div>
              <div style={{ position: "absolute", bottom: 14, left: 16, right: 16,
                            ...SANS, fontSize: 11, letterSpacing: "0.18em",
                            color: "rgba(244,239,228,0.86)", fontWeight: 600,
                            textTransform: "uppercase" }}>
                Concept preview · V2 AI Visualisation
              </div>
            </div>
          </StageBlock>
        </Reveal>
      </div>

      {/* V2 disclosure — never fine-print */}
      <Reveal delay={320}>
        <div style={{ marginTop: 34, padding: "18px 22px",
                      border: `1px solid rgba(200,169,126,0.36)`,
                      background: "rgba(200,169,126,0.08)",
                      maxWidth: 780 }}>
          <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.22em",
                        color: COLORS.gold, fontWeight: 700, marginBottom: 6,
                        textTransform: "uppercase" }}>
            Not included in the current Foundation Beta
          </div>
          <div style={{ ...SANS, fontSize: 14, lineHeight: 1.65,
                        color: "rgba(244,239,228,0.82)" }}>
            AI Visualisation is planned for V2 following completion of the Foundation Beta. Today's Beta ships with 2D Design, interactive 3D Visualise, materials, templates, pricing, proposals and the client approval workflow.
          </div>
        </div>
      </Reveal>

      {/* Foundation Beta connection + CTA */}
      <Reveal delay={400}>
        <div style={{ marginTop: 40, textAlign: "center" }}>
          <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.24em",
                        color: COLORS.gold, fontWeight: 700,
                        textTransform: "uppercase", marginBottom: 12 }}>
            The Beta is just the beginning
          </div>
          <p style={{ ...SANS, color: "rgba(244,239,228,0.75)", fontSize: 15,
                      lineHeight: 1.7, maxWidth: 640, margin: "0 auto 30px" }}>
            Foundation Studios will help shape the workflow leading into V2, including Subtend Studio's upcoming AI Visualisation experience.
          </p>
          <button data-testid="apply-v2-cta"
                  onClick={() => {
                    trackEvent("v2_teaser_cta_click", {});
                    document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  style={{ ...SANS, background: COLORS.gold, color: COLORS.bgDark,
                           border: "none", padding: "16px 30px", fontSize: 14,
                           fontWeight: 600, letterSpacing: "0.08em",
                           cursor: "pointer", textTransform: "uppercase" }}>
            Apply for Foundation Access →
          </button>
          <div style={{ ...SANS, marginTop: 16, fontSize: 12.5,
                        color: "rgba(244,239,228,0.55)",
                        letterSpacing: "0.06em" }}>
            <span style={{ marginRight: 8 }}>🇦🇺</span>Australia — Paid Private Beta
            <span style={{ margin: "0 12px", opacity: 0.4 }}>·</span>
            <span style={{ marginRight: 8 }}>🇬🇧</span>United Kingdom — Early Access
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

// Shared stage container for the 2D → 3D → AI progression.
// `emphasis` tightens the border. `hero` marks stage 3 (the AI preview)
// with a gold border and soft glow — but its status LABEL still reads
// "Coming in V2", never a current-feature badge.
function StageBlock({ kicker, title, body, statusLabel, statusTone,
                       emphasis = false, hero = false, children }) {
  const gold = statusTone === "v2" || hero;
  return (
    <div style={{
           background: hero
             ? "linear-gradient(180deg,#1F1811 0%,#100E0C 100%)"
             : "linear-gradient(180deg,#1B1712 0%,#100E0C 100%)",
           border: `1px solid ${gold ? COLORS.gold : (emphasis ? "rgba(200,169,126,0.38)" : COLORS.cardEdge)}`,
           boxShadow: hero ? "0 40px 100px -40px rgba(200,169,126,0.36)" : "none",
           display: "grid", gap: 0,
           gridTemplateColumns: "minmax(0, 1fr)",
         }}
         className={hero ? "apply-stage-hero" : "apply-stage"}>
      <div style={{ padding: "22px 24px 8px" }}>
        <div style={{ ...SANS, fontSize: 10.5, letterSpacing: "0.22em",
                      color: COLORS.gold, fontWeight: 700,
                      textTransform: "uppercase" }}>
          {kicker}
        </div>
        <div style={{ ...SERIF, fontSize: 30, color: COLORS.ivory,
                      lineHeight: 1.15, marginTop: 6 }}>{title}</div>
        <p style={{ ...SANS, color: "rgba(244,239,228,0.72)", fontSize: 14,
                    lineHeight: 1.65, marginTop: 10, marginBottom: 14,
                    maxWidth: 620 }}>{body}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "6px 12px",
                      border: `1px solid ${statusTone === "available" ? COLORS.gold : "rgba(200,169,126,0.5)"}`,
                      background: statusTone === "available"
                        ? "rgba(200,169,126,0.16)" : "transparent",
                      ...SANS, fontSize: 10, letterSpacing: "0.20em",
                      color: COLORS.gold, fontWeight: 700,
                      textTransform: "uppercase" }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%",
                         background: COLORS.gold,
                         opacity: statusTone === "available" ? 1 : 0.6 }}
                aria-hidden="true"/>
          <span>{statusLabel}</span>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}


// ── Price feature (light) ────────────────────────────────────────
function PriceFeatureSection() {
  return (
    <Section tone="ivory" testid="apply-section-price" padY={110}>
      <div className="apply-feature-grid"
           style={{ display: "grid", gap: 48, alignItems: "center" }}>
        <Reveal>
          <Eyebrow tone="deep">Know whether the job is worth doing</Eyebrow>
          <H2>Know the numbers while you're designing.</H2>
          <Lede>
            Design decisions and commercial decisions shouldn't live in separate files. Subtend Studio keeps materials, labour, trades, sell price and margin connected to the project as it develops.
          </Lede>
          <div style={{ ...SERIF, fontSize: 26, lineHeight: 1.25, color: COLORS.ink,
                        marginTop: 26, maxWidth: 520 }}>
            Your client sees their investment.<br/>
            <span style={{ color: COLORS.goldDeep, fontStyle: "italic" }}>You see the margin.</span>
          </div>
          <p style={{ ...SANS, color: COLORS.inkSoft, fontSize: 13.5,
                      lineHeight: 1.65, margin: "20px 0 0", maxWidth: 520 }}>
            Your internal commercial information stays inside the Studio while client-facing views show only what the client should see.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <PriceMock/>
        </Reveal>
      </div>
      <MidCta country={null}
              copy="Think Subtend Studio could work in your studio?"
              cta="Apply for Foundation Access →"
              tone="ivory"/>
    </Section>
  );
}

// ── Proposal + Approval (dark) ───────────────────────────────────
function ProposalFeatureSection() {
  return (
    <Section tone="dark" testid="apply-section-proposal" padY={110}>
      <Reveal>
        <Eyebrow>Turn the design into the sale</Eyebrow>
        <H2 dark>Turn the design into the sale.</H2>
        <Lede dark>
          Bring the design, scope, inclusions and client investment into a polished proposal without rebuilding the same project in another document. Then keep client feedback, requested changes and approval attached to the same project.
        </Lede>
        <div style={{ ...SERIF, fontStyle: "italic", color: COLORS.gold,
                      fontSize: 22, marginTop: 22, maxWidth: 640 }}>
          From “What could we build here?” to <span style={{ color: COLORS.ivory }}>“Approved.”</span>
        </div>
      </Reveal>
      <div style={{ marginTop: 44, display: "grid", gap: 18 }}>
        <Reveal delay={80}>
          <div style={{ display: "flex", gap: 14, marginBottom: 6, alignItems: "center" }}>
            <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.20em",
                          color: COLORS.gold, fontWeight: 700 }}>STUDIO VIEW</div>
            <div style={{ flex: 1, height: 1, background: "rgba(200,169,126,0.28)" }}/>
            <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.20em",
                          color: COLORS.gold, fontWeight: 700 }}>→ CLIENT VIEW</div>
          </div>
          <ProposalPortalMock/>
        </Reveal>
      </div>
    </Section>
  );
}

// ── Head-start (light) — Materials + Templates ───────────────────
function HeadStartSection() {
  const templateNames = [
    "Modern Signature","Coastal Hamptons","Fireplace Centrepiece",
    "Floating Minimal","Full-Wall Joinery","Asymmetrical Display",
    "Timber Architectural","Stone Feature","Entertainment / AV","Contemporary Display",
  ];
  return (
    <Section tone="warm" testid="apply-section-headstart" padY={110}>
      <Reveal>
        <Eyebrow tone="deep">Don't start from zero</Eyebrow>
        <H2>Start with a Studio, not an empty database.</H2>
        <Lede>
          Two libraries are built in from your very first project — and they grow as your studio does.
        </Lede>
      </Reveal>
      <div className="apply-headstart-grid"
           style={{ display: "grid", gap: 42, marginTop: 46 }}>
        <Reveal delay={80}>
          <div style={{ background: "#FFFDF6", border: `1px solid ${COLORS.cardEdge}`,
                        padding: "28px 26px" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "baseline", marginBottom: 6 }}>
              <div>
                <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.18em",
                              color: COLORS.goldDeep, fontWeight: 700 }}>
                  STARTER MATERIALS
                </div>
                <div style={{ ...SERIF, fontSize: 30, color: COLORS.ink, marginTop: 4 }}>
                  32 materials in 8 categories
                </div>
              </div>
              <div style={{ ...SERIF, fontSize: 42, color: COLORS.gold, lineHeight: 1 }}>32</div>
            </div>
            <div style={{ ...SANS, fontSize: 13, color: COLORS.inkSoft,
                          letterSpacing: "0.02em", margin: "8px 0 18px" }}>
              Timber · Stone · Concrete · Acoustic · Cabinetry · Panels · Paint · Specialty
            </div>
            <MaterialsGrid/>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <div style={{ background: "#FFFDF6", border: `1px solid ${COLORS.cardEdge}`,
                        padding: "28px 26px" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "baseline", marginBottom: 20 }}>
              <div>
                <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.18em",
                              color: COLORS.goldDeep, fontWeight: 700 }}>
                  STARTER MEDIA-WALL DESIGNS
                </div>
                <div style={{ ...SERIF, fontSize: 30, color: COLORS.ink, marginTop: 4 }}>
                  10 designs ready to adapt
                </div>
              </div>
              <div style={{ ...SERIF, fontSize: 42, color: COLORS.gold, lineHeight: 1 }}>10</div>
            </div>
            <TemplateGalleryMock/>
            <div style={{ ...SANS, fontSize: 12.5, color: COLORS.inkSoft,
                          marginTop: 16, lineHeight: 1.6 }}>
              {templateNames.join(" · ")}
            </div>
          </div>
        </Reveal>
      </div>
      <Reveal>
        <p style={{ ...SANS, color: COLORS.inkSoft, fontSize: 14, lineHeight: 1.65,
                    textAlign: "center", marginTop: 40, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
          Add your own materials, suppliers, pricing and designs as your Subtend Studio library grows.
        </p>
      </Reveal>
      <MidCta country={null}
              copy="Enough capability to help your next project?"
              cta="See if your studio is a fit →"
              tone="warm"/>
    </Section>
  );
}

// ── Before / With Luxe (dark) ────────────────────────────────────
function ComparisonSection() {
  const before = [
    "Design software",
    "Spreadsheet",
    "Pricing calculations",
    "Proposal document",
    "Email attachments",
    "Client changes",
    "Update everything again",
  ];
  const withLuxe = [
    "Brief", "Design", "Visualise", "Price", "Proposal", "Approval",
  ];
  return (
    <Section tone="dark" testid="apply-section-comparison" padY={110}>
      <Reveal>
        <Eyebrow>A better way to run the project</Eyebrow>
        <H2 dark>A media wall shouldn't take five different tools to sell.</H2>
        <Lede dark>
          The client brief lives in one place. The drawing in another. Pricing ends up in a spreadsheet. The proposal becomes another document. Changes arrive through email and messages. Subtend Studio connects the process.
        </Lede>
        <div style={{ ...SERIF, fontStyle: "italic", color: COLORS.gold,
                      fontSize: 22, marginTop: 22, maxWidth: 640 }}>
          One project. One workflow. One source of truth.
        </div>
      </Reveal>
      <div className="apply-compare-grid"
           style={{ display: "grid", gap: 22, marginTop: 42 }}>
        <Reveal delay={80}>
          <div style={{ background: "rgba(244,239,228,0.03)",
                        border: "1px solid rgba(244,239,228,0.10)",
                        padding: "30px 28px" }}>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.24em",
                          color: "rgba(244,239,228,0.55)", fontWeight: 700, marginBottom: 8 }}>
              BEFORE SUBTEND
            </div>
            <div style={{ ...SERIF, fontSize: 28, color: "rgba(244,239,228,0.55)",
                          marginBottom: 22, fontStyle: "italic" }}>
              The same project, rebuilt everywhere.
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {before.map((b, i) => (
                <li key={b}
                    style={{ ...SANS, display: "flex", alignItems: "center", gap: 12,
                             color: "rgba(244,239,228,0.58)", fontSize: 14.5,
                             padding: "12px 0",
                             borderBottom: i === before.length - 1 ? "none" : "1px solid rgba(244,239,228,0.08)",
                             textDecoration: i > 4 ? "line-through" : "none",
                             textDecorationColor: "rgba(176,101,76,0.5)" }}>
                  <span style={{ width: 20, height: 1.5,
                                  background: "rgba(244,239,228,0.20)" }}/>
                  <span style={{ flex: 1 }}>{b}</span>
                  {i < before.length - 1 && (
                    <span style={{ ...SANS, color: "rgba(244,239,228,0.32)", fontSize: 12 }}>↓</span>
                  )}
                </li>
              ))}
            </ul>
            <div style={{ ...SANS, fontSize: 12.5, color: "rgba(244,239,228,0.5)",
                          marginTop: 18, fontStyle: "italic" }}>
              The same project rebuilt across disconnected tools.
            </div>
          </div>
        </Reveal>
        <Reveal delay={160}>
          <div style={{ background: "linear-gradient(180deg, #1E1B15 0%, #100E0C 100%)",
                        border: `1px solid ${COLORS.gold}`,
                        boxShadow: "0 30px 60px -30px rgba(200,169,126,0.30)",
                        padding: "30px 28px", position: "relative" }}>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.24em",
                          color: COLORS.gold, fontWeight: 700, marginBottom: 8 }}>
              WITH SUBTEND
            </div>
            <div style={{ ...SERIF, fontSize: 28, color: COLORS.ivory,
                          marginBottom: 22 }}>
              One project. First idea to approval.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
              {withLuxe.map((s, i) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ ...SANS, background: COLORS.ivory, color: COLORS.bgDark,
                                padding: "10px 16px", fontSize: 13.5,
                                letterSpacing: "0.04em", fontWeight: 600 }}>
                    {s}
                  </div>
                  {i < withLuxe.length - 1 && (
                    <div style={{ color: COLORS.gold, ...SANS, fontSize: 14 }}>→</div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ ...SANS, fontSize: 12.5, color: "rgba(244,239,228,0.72)",
                          marginTop: 22, fontStyle: "italic" }}>
              One connected project from first idea to client approval.
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ── Mid-page CTA (restrained) ────────────────────────────────────
function MidCta({ copy, cta, tone = "dark", country = null }) {
  const dark = tone === "dark";
  return (
    <Reveal>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                    gap: 20, justifyContent: "space-between",
                    marginTop: 60, padding: "22px 26px",
                    background: dark ? "rgba(200,169,126,0.06)" : "rgba(28,28,28,0.04)",
                    border: `1px solid ${dark ? "rgba(200,169,126,0.22)" : "rgba(28,28,28,0.12)"}` }}>
        <div style={{ ...SERIF, fontSize: 22, lineHeight: 1.2,
                      color: dark ? COLORS.ivory : COLORS.ink,
                      fontStyle: "italic" }}>
          {copy}
        </div>
        <button data-testid={`apply-mid-cta-${cta.slice(0,10).replace(/\s+/g,"-").toLowerCase()}`}
                onClick={() => scrollToApply(country)}
                style={{ ...SANS, background: dark ? COLORS.gold : COLORS.ink,
                         color: dark ? COLORS.bgDark : COLORS.ivory,
                         border: "none", padding: "12px 22px", fontSize: 13,
                         letterSpacing: "0.06em", fontWeight: 600, cursor: "pointer" }}>
          {cta}
        </button>
      </div>
    </Reveal>
  );
}

// ── Target market (warm) ─────────────────────────────────────────
function TargetMarketSection() {
  return (
    <Section tone="warm" testid="apply-section-target-market" padY={96}>
      <Reveal>
        <Eyebrow tone="deep">Who Subtend Studio is for</Eyebrow>
        <H2>Built for businesses that design what they install.</H2>
        <Lede>
          Subtend Studio is initially being developed around the real workflow of premium media-wall projects — not retrofitted from generic CRM or quoting software.
        </Lede>
      </Reveal>
      <div className="apply-target-grid"
           style={{ display: "grid", gap: 18, marginTop: 38 }}>
        <Reveal delay={60}>
          <div style={{ background: "linear-gradient(180deg, #1F1B15 0%, #100E0C 100%)",
                        border: `1px solid ${COLORS.gold}`, padding: "26px 24px",
                        boxShadow: "0 24px 60px -30px rgba(200,169,126,0.30)" }}>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.20em",
                          color: COLORS.gold, fontWeight: 700, marginBottom: 6 }}>
              PRIMARY MARKET
            </div>
            <div style={{ ...SERIF, fontSize: 28, color: COLORS.ivory, marginBottom: 10 }}>
              Media-wall specialists
            </div>
            <p style={{ ...SANS, color: "rgba(244,239,228,0.72)", fontSize: 14,
                        lineHeight: 1.6, margin: 0 }}>
              Studios who design, quote and install premium media walls in high-end residential interiors.
            </p>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <div style={{ background: "#FFFDF6", border: `1px solid ${COLORS.cardEdge}`,
                        padding: "26px 24px" }}>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.20em",
                          color: COLORS.goldDeep, fontWeight: 700, marginBottom: 12 }}>
              SUPPORTING MARKETS
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {["Bespoke joinery studios",
                "Residential AV integrators",
                "Home-cinema businesses"].map((x, i, arr) => (
                <li key={x} style={{ ...SANS, display: "flex", gap: 10, alignItems: "center",
                                     padding: "10px 0",
                                     borderBottom: i === arr.length - 1 ? "none" : `1px solid ${COLORS.cardEdge}`,
                                     color: COLORS.ink, fontSize: 15, fontWeight: 500 }}>
                  <span style={{ color: COLORS.gold, fontSize: 10 }}>◆</span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
            <p style={{ ...SANS, color: COLORS.inkSoft, fontSize: 12.5,
                        lineHeight: 1.6, margin: "16px 0 0" }}>
              Adjacent trades that already work project-by-project on bespoke interiors will benefit from the same workflow.
            </p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ── Foundation Studio (dark architectural) ───────────────────────
function FoundationSection() {
  return (
    <Section id="foundation" tone="dark" testid="apply-section-foundation" padY={110}>
      <Reveal>
        <div style={{ maxWidth: 780 }}>
          <Eyebrow>Foundation Studio Program</Eyebrow>
          <H2 dark>Help shape the software your industry has been missing.</H2>
          <Lede dark>
            We're looking for a small number of established media-wall, bespoke joinery and residential AV businesses to help shape the first commercial version of Subtend Studio.
          </Lede>
          <p style={{ ...SANS, color: "rgba(244,239,228,0.6)", fontSize: 15,
                      lineHeight: 1.7, marginTop: 16, maxWidth: 640 }}>
            This isn't simply early access to another piece of software. Foundation Studios will work closely with us while Subtend Studio is refined around real projects, real quoting workflows and real client presentations — and will also help shape upcoming capabilities including AI-assisted design and premium visualisation.
          </p>
        </div>
      </Reveal>
      <div className="apply-foundation-grid"
           style={{ display: "grid", gap: 22, marginTop: 46 }}>
        {/* AUSTRALIA */}
        <Reveal delay={80}>
          <div style={{ background: "linear-gradient(180deg, #1F1B15 0%, #100E0C 100%)",
                        border: `1px solid ${COLORS.gold}`,
                        boxShadow: "0 40px 80px -40px rgba(200,169,126,0.35)",
                        padding: "36px 32px", height: "100%" }}
               data-testid="apply-foundation-au">
            <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 12 }}>🇦🇺</div>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.20em",
                          color: COLORS.gold, fontWeight: 700 }}>AUSTRALIA · PAID PRIVATE BETA</div>
            <div style={{ ...SERIF, fontSize: 32, color: COLORS.ivory,
                          margin: "6px 0 8px" }}>
              Become an Australian Foundation Studio.
            </div>
            <p style={{ ...SANS, color: "rgba(244,239,228,0.72)",
                        fontSize: 14.5, lineHeight: 1.65, margin: "0 0 22px", maxWidth: 460 }}>
              We're inviting a limited number of Australian studios into the first commercial Subtend Studio beta.
            </p>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.18em",
                          color: COLORS.gold, fontWeight: 700, marginBottom: 10 }}>
              FOUNDATION STUDIOS RECEIVE
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {["Personal onboarding","Help configuring your Studio","Direct founder support",
                "Foundation pricing","Early access to new capabilities","Direct influence on the product roadmap"].map((x, i, arr) => (
                <li key={x} style={{ ...SANS, display: "flex", gap: 10, alignItems: "flex-start",
                                     padding: "8px 0",
                                     borderBottom: i === arr.length - 1 ? "none" : "1px solid rgba(200,169,126,0.14)",
                                     color: "rgba(244,239,228,0.82)", fontSize: 13.5 }}>
                  <span style={{ color: COLORS.gold, marginTop: 5, fontSize: 10 }}>◆</span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 24, ...SANS, display: "inline-block",
                          padding: "6px 14px",
                          border: `1px solid ${COLORS.gold}`, color: COLORS.gold,
                          fontSize: 10.5, letterSpacing: "0.18em", fontWeight: 700 }}>
              LIMITED INITIAL PLACES
            </div>
            <div style={{ marginTop: 22 }}>
              <button data-testid="apply-foundation-au-cta"
                      onClick={() => scrollToApply("AU")}
                      style={{ ...SANS, background: COLORS.gold, color: COLORS.bgDark,
                               border: "none", padding: "14px 26px", fontSize: 13.5,
                               letterSpacing: "0.06em", fontWeight: 700, cursor: "pointer",
                               width: "100%" }}>
                Apply for Australian Beta ↓
              </button>
              <div style={{ ...SANS, fontSize: 11.5, color: "rgba(244,239,228,0.5)",
                            marginTop: 10, textAlign: "center" }}>
                Applying doesn't commit you to purchasing Subtend Studio.
              </div>
            </div>
          </div>
        </Reveal>
        {/* UK */}
        <Reveal delay={160}>
          <div style={{ background: "rgba(244,239,228,0.03)",
                        border: "1px solid rgba(244,239,228,0.14)",
                        padding: "36px 32px", height: "100%" }}
               data-testid="apply-foundation-uk">
            <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 12 }}>🇬🇧</div>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.20em",
                          color: COLORS.gold, fontWeight: 700 }}>UNITED KINGDOM · EARLY ACCESS</div>
            <div style={{ ...SERIF, fontSize: 32, color: COLORS.ivory,
                          margin: "6px 0 8px" }}>
              Join the UK Foundation Studio list.
            </div>
            <p style={{ ...SANS, color: "rgba(244,239,228,0.72)",
                        fontSize: 14.5, lineHeight: 1.65, margin: "0 0 22px", maxWidth: 460 }}>
              We're preparing Subtend Studio for UK studios, including GBP pricing, VAT and UK localisation.
            </p>
            <p style={{ ...SANS, color: "rgba(244,239,228,0.66)",
                        fontSize: 13.5, lineHeight: 1.7, margin: "0 0 22px" }}>
              Early Access studios will be first in line for the UK beta and will have the opportunity to help shape the UK version before wider release.
            </p>
            <div style={{ ...SANS, display: "inline-block", padding: "6px 14px",
                          border: `1px solid ${COLORS.gold}`, color: COLORS.gold,
                          fontSize: 10.5, letterSpacing: "0.18em", fontWeight: 700,
                          marginBottom: 22 }}>
              UK EARLY ACCESS NOW OPEN
            </div>
            <div style={{ marginTop: 4 }}>
              <button data-testid="apply-foundation-uk-cta"
                      onClick={() => scrollToApply("UK")}
                      style={{ ...SANS, background: COLORS.ivory, color: COLORS.bgDark,
                               border: "none", padding: "14px 26px", fontSize: 13.5,
                               letterSpacing: "0.06em", fontWeight: 700, cursor: "pointer",
                               width: "100%" }}>
                Join UK Early Access ↓
              </button>
              <div style={{ ...SANS, fontSize: 11.5, color: "rgba(244,239,228,0.5)",
                            marginTop: 10, textAlign: "center" }}>
                No payment or commitment is required to join Early Access.
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ── Application transition + form ────────────────────────────────
function ApplicationBlock({ country, submittedCountry, setCountry, setSubmittedCountry }) {
  return (
    <Section id="apply-form" tone="warm" testid="apply-section-application" padY={96}>
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Eyebrow tone="deep">Want to be one of the first?</Eyebrow>
          <H2>Apply for Subtend Studio.</H2>
          <p style={{ ...SANS, color: COLORS.inkSoft, fontSize: 16, lineHeight: 1.65,
                      margin: "0 auto", maxWidth: 520 }}>
            Tell us a little about your studio. It takes less than two minutes.
          </p>
        </div>
      </Reveal>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        {submittedCountry ? (
          <SuccessPanel country={submittedCountry}/>
        ) : !country ? (
          <div style={{ background: "rgba(255,255,255,0.6)",
                        border: `1px solid ${COLORS.cardEdge}`,
                        padding: "32px 30px" }}>
            <CountrySelector selected={country} onSelect={setCountry}/>
            <div style={{ ...SANS, color: COLORS.inkSoft, fontSize: 13, lineHeight: 1.65 }}>
              Never blur the two: Australia is a paid private beta with foundation-studio pricing. The UK program is early-access only while GBP and VAT localisation are being prepared.
            </div>
          </div>
        ) : (
          <ApplicationForm
            country={country}
            onBack={() => setCountry(null)}
            onSubmitted={() => setSubmittedCountry(country)}
          />
        )}
      </div>
    </Section>
  );
}

// ── Footer ───────────────────────────────────────────────────────
function PublicFooter() {
  return (
    <footer data-testid="apply-footer" style={{ background: COLORS.bgDeep, color: "rgba(244,239,228,0.7)" }}>
      <div className="apply-shell" style={{ padding: "62px 28px 42px" }}>
        <div className="apply-footer-grid"
             style={{ display: "grid", gap: 42, marginBottom: 42 }}>
          <div>
            <div style={{ ...SANS, color: COLORS.ivory, marginBottom: 8,
                          letterSpacing: "0.32em", fontWeight: 700,
                          fontSize: 15, lineHeight: 1.05 }}>
              <div>SUBTEND</div>
              <div style={{ color: COLORS.gold }}>STUDIO</div>
            </div>
            <div style={{ ...SANS, letterSpacing: "0.08em", fontSize: 12,
                          color: COLORS.gold, marginTop: 14 }}>
              Design · Visualise · Price · Sell
            </div>
            <div style={{ ...SANS, marginTop: 14, fontSize: 12.5,
                          color: "rgba(244,239,228,0.62)", lineHeight: 1.6,
                          maxWidth: 320 }}>
              Design-to-proposal software built for media-wall businesses.
            </div>
            <div style={{ ...SANS, marginTop: 14, fontSize: 12,
                          color: "rgba(244,239,228,0.5)" }}>
              Australia · United Kingdom
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 22 }}>
            <div>
              <div style={{ ...SANS, letterSpacing: "0.16em", fontSize: 10.5,
                            color: COLORS.gold, fontWeight: 700, marginBottom: 12 }}>
                PRODUCT
              </div>
              {[
                { l: "Features", id: "features" },
                { l: "How It Works", id: "workflow" },
                { l: "For Studios", id: "foundation" },
                { l: "Private Beta", id: "foundation" },
              ].map(x => (
                <div key={x.l}
                     onClick={() => scrollToId(x.id)}
                     style={{ ...SANS, fontSize: 13, color: "rgba(244,239,228,0.78)",
                              padding: "6px 0", cursor: "pointer" }}>
                  {x.l}
                </div>
              ))}
            </div>
            <div>
              <div style={{ ...SANS, letterSpacing: "0.16em", fontSize: 10.5,
                            color: COLORS.gold, fontWeight: 700, marginBottom: 12 }}>
                COMPANY
              </div>
              <Link to="/login" style={{ ...SANS, fontSize: 13,
                                          color: "rgba(244,239,228,0.78)",
                                          textDecoration: "none",
                                          display: "block", padding: "6px 0" }}>Login</Link>
              <Link to="/terms" style={{ ...SANS, fontSize: 13,
                                          color: "rgba(244,239,228,0.78)",
                                          textDecoration: "none",
                                          display: "block", padding: "6px 0" }}>Terms</Link>
              <Link to="/privacy" style={{ ...SANS, fontSize: 13,
                                            color: "rgba(244,239,228,0.78)",
                                            textDecoration: "none",
                                            display: "block", padding: "6px 0" }}>Privacy</Link>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(200,169,126,0.14)",
                      paddingTop: 22, ...SANS, fontSize: 11.5,
                      color: "rgba(244,239,228,0.45)" }}>
          © {new Date().getFullYear()} Subtend Studio
        </div>
      </div>
    </footer>
  );
}

// ── Mobile sticky CTA ────────────────────────────────────────────
function MobileStickyCta({ hidden }) {
  const [pastHero, setPastHero] = useState(false);
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (hidden || !pastHero) return null;
  return (
    <div data-testid="apply-sticky-cta"
         className="apply-mobile-sticky"
         style={{ position: "fixed", left: 12, right: 12, bottom: "calc(12px + env(safe-area-inset-bottom))",
                  background: COLORS.gold, color: COLORS.bgDark,
                  padding: "14px 22px", ...SANS, fontSize: 14, fontWeight: 700,
                  letterSpacing: "0.06em", textAlign: "center", cursor: "pointer",
                  zIndex: 30, boxShadow: "0 24px 40px -18px rgba(0,0,0,0.6)" }}
         onClick={() => scrollToApply()}>
      Apply for Access →
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────
export default function ApplyPage() {
  const [country, setCountry] = useState(null);
  const [submittedCountry, setSubmittedCountry] = useState(null);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = "Subtend Studio | Design-to-Proposal Software for Media-Wall Businesses";
      const setMeta = (name, content, attr = "name") => {
        let el = document.querySelector(`meta[${attr}="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      const desc = "Design, visualise, price and sell premium media-wall projects in one connected workflow. Apply for Subtend Studio Foundation access in Australia or UK Early Access.";
      setMeta("description", desc);
      setMeta("og:title", "Subtend Studio | Design-to-Proposal Software for Media-Wall Businesses", "property");
      setMeta("og:description", desc, "property");
      setMeta("og:type", "website", "property");
    }
    trackEvent("apply_page_view", {});
  }, []);

  // Fire section-viewed analytics from IntersectionObserver — one-shot per section.
  useEffect(() => {
    const sections = [
      { id: "workflow",                   ev: "workflow_viewed" },
      { id: "apply-section-design",       ev: "design_feature_viewed" },
      { id: "apply-section-visualise",    ev: "visualise_feature_viewed" },
      { id: "apply-section-price",        ev: "pricing_feature_viewed" },
      { id: "foundation",                 ev: "foundation_section_viewed" },
    ];
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !seen.has(e.target.id)) {
          seen.add(e.target.id);
          const s = sections.find(x => x.id === e.target.id
                                     || (x.id === "apply-section-design"     && e.target.dataset?.testid === "apply-section-design")
                                     || (x.id === "apply-section-visualise"  && e.target.dataset?.testid === "apply-section-visualise")
                                     || (x.id === "apply-section-price"      && e.target.dataset?.testid === "apply-section-price"));
          if (s) trackEvent(s.ev, {});
        }
      });
    }, { threshold: 0.35 });
    sections.forEach(s => {
      const el = document.getElementById(s.id) ||
                 document.querySelector(`[data-testid="${s.id}"]`);
      if (el) { el.id = el.id || s.id; io.observe(el); }
    });
    return () => io.disconnect();
  }, []);

  // Preselect country when a card CTA fires the CustomEvent.
  useEffect(() => {
    const onPre = (e) => {
      const c = e?.detail?.country;
      if ((c === "AU" || c === "UK") && !submittedCountry) setCountry(c);
    };
    window.addEventListener("luxe:apply-preselect", onPre);
    return () => window.removeEventListener("luxe:apply-preselect", onPre);
  }, [submittedCountry]);

  return (
    <div data-testid="apply-page"
         style={{ background: COLORS.bgDark, minHeight: "100vh",
                  color: COLORS.ivory, overflowX: "hidden" }}>
      {/* Scoped styles for /apply only. Every override is namespaced so nothing else breaks. */}
      <style>{`
        .apply-shell { max-width: 1200px; margin: 0 auto; }
        @media (max-width: 640px) {
          .apply-shell { padding-left: 18px !important; padding-right: 18px !important; }
        }
        /* Every /apply grid defaults to a single, shrinkable column on
           mobile — media queries below promote them to multi-column on
           wider viewports. minmax(0, 1fr) prevents children (SVGs,
           long strings, wide UI mocks) from forcing horizontal overflow. */
        .apply-hero-grid,
        .apply-workflow-grid,
        .apply-feature-grid,
        .apply-headstart-grid,
        .apply-compare-grid,
        .apply-target-grid,
        .apply-foundation-grid,
        .apply-two-mock,
        .apply-footer-grid,
        .apply-materials-grid,
        .apply-v2-stages {
          grid-template-columns: minmax(0, 1fr);
        }
        /* Country card grid */
        @media (min-width: 780px) {
          .apply-country-grid { grid-template-columns: 1fr 1fr !important; }
          .apply-grid-two { grid-template-columns: 1fr 1fr !important; gap: 20px !important; }
        }
        /* Hero: single-col mobile, two-col desktop */
        @media (min-width: 960px) {
          .apply-hero-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 64px !important; }
        }
        @media (max-width: 960px) {
          .apply-hero-float { position: static !important; margin-top: 24px; width: 100% !important; }
          .apply-hero-comp { min-height: 0 !important; }
          .apply-hero-comp .apply-hero-ai-wrap {
            position: relative !important;
            top: auto !important; right: auto !important;
            width: 100% !important; max-width: 100% !important;
            transform: none !important;
            margin-bottom: 18px;
          }
          .apply-hero-comp .apply-hero-window {
            position: relative !important;
            top: auto !important; left: auto !important;
            margin-top: 0 !important; margin-left: 0 !important;
            width: 100% !important; max-width: 100% !important;
          }
          .apply-hero-comp .apply-hero-glow { display: none !important; }
          .apply-hero-comp > div[style*="marginRight"] { margin-right: 0 !important; max-width: 100% !important; }
          .apply-hero-script {
            position: relative !important;
            top: auto !important; right: auto !important;
            transform: none !important;
            text-align: left !important;
            font-size: 22px !important;
            margin-bottom: 10px;
          }
          .apply-hero-script svg { margin-left: 0 !important; }
        }
        /* Nav */
        @media (max-width: 900px) { .apply-nav-desktop { display: none !important; } }
        /* Feature: alternating two-col */
        @media (min-width: 960px) {
          .apply-feature-grid { grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr) !important; gap: 64px !important; }
          .apply-feature-reverse { grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) !important; }
          .apply-feature-reverse > *:first-child { order: 1; }
          .apply-feature-reverse > *:last-child  { order: 0; }
        }
        /* Workflow: 6-col desktop, stacked mobile */
        @media (min-width: 720px)  { .apply-workflow-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; } }
        @media (min-width: 1080px) { .apply-workflow-grid { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; } }
        /* Head-start */
        @media (min-width: 960px)  { .apply-headstart-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 32px !important; } }
        /* Comparison */
        @media (min-width: 900px)  { .apply-compare-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 32px !important; } }
        /* Target market */
        @media (min-width: 900px)  { .apply-target-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 24px !important; } }
        /* AI chain: 3-col at wide desktop, 1-col mobile */
        @media (min-width: 900px)  { .apply-ai-chain { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; gap: 22px !important; } }
        /* V2 teaser stages: single column at ALL widths so each stage
           can breathe and use full width. Visual hierarchy grows as
           you scroll (2D → 3D → AI hero image). */
        .apply-v2-stages { max-width: 1080px; margin-left: auto; margin-right: auto; }
        /* Foundation */
        @media (min-width: 900px)  { .apply-foundation-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 24px !important; } }
        /* Two-mock inline stack */
        @media (min-width: 1080px) { .apply-two-mock { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 24px !important; } }
        /* Materials grid */
        @media (min-width: 520px)  { .apply-materials-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; } }
        /* Footer */
        @media (min-width: 780px)  { .apply-footer-grid { grid-template-columns: 1.3fr minmax(0, 1fr) !important; align-items: start; } }
        /* Hide mobile sticky on ≥720 */
        @media (min-width: 720px)  { .apply-mobile-sticky { display: none !important; } }
        /* Anchor scroll offset for sticky header */
        :target { scroll-margin-top: 78px; }
        /* Hero headline: on wide screens keep "Design. Visualise." and
           "Price. Sell." on two elegant lines. On mobile, force a break
           after every word so the four-word promise reads vertically. */
        .apply-hero-br-mobile { display: none; }
        @media (max-width: 640px) {
          .apply-hero-br-mobile { display: inline; }
        }
      `}</style>

      <PublicHeader/>
      <HeroSection/>
      <V2AiTeaserSection/>
      <WorkflowSection/>
      <DesignFeatureSection/>
      <VisualiseFeatureSection/>
      <PriceFeatureSection/>
      <ProposalFeatureSection/>
      <HeadStartSection/>
      <ComparisonSection/>
      <TargetMarketSection/>
      <FoundationSection/>
      <ApplicationBlock
        country={country}
        submittedCountry={submittedCountry}
        setCountry={setCountry}
        setSubmittedCountry={setSubmittedCountry}/>
      <PublicFooter/>
      <MobileStickyCta hidden={!!submittedCountry}/>
    </div>
  );
}
